import axios, { AxiosResponse } from "axios";
import { EnvironmentRegistry } from "../registry/environmentRegistry";
import { getConfigFromClientConfig } from "../bannerServices";
import { getLob, signInForUserAndEnv } from "../authenticationService";
import { configurationAttributeType } from "../../types";
import { validateWithOtp } from "../../stateManagement/actions/validateOtpPopupActions";
import { manageUpdateAccessObj } from "../../features/manageUpdateAccess/manageUpdateAccessTypes";
import { getMetaDataConfig, validateMetaDataResponse } from "../uiConfigService";
import { ChannelkartNetworkGet, META_DATA_BATCH_API } from "../networkService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { metaDataBatchPayload } from "../../types/types";
import { DataSyncManagerConfig } from "../../components/auth/dashboardAuth";
import { switchCase } from "@babel/types";
import { fetchOutletDetails } from "../../features/createSecondaryOrder/CreateSecondaryOrderService";
import { deepClone } from "@mui/x-data-grid/utils/utils";

interface ILobData {
  lastModifiedTime: string,
  creationTime: string,
  status: string,
  deploymentCountry: string,
  user: string,
  key: string,
  lob: string,
  organizationName: string,
  workEmail: string,
  onboardingStep: number,
  isOnboarding: boolean,
  mandatoryConfigSetupProgress:number
}

const BASE_URL = "https://xly68awt34.execute-api.ap-south-1.amazonaws.com";

export interface IOnboardingData {
  step: number,
  isOnboarding: boolean
  mandatoryConfigSetupProgress:number
}

export const getOnboardingConfig = async () : Promise<IOnboardingData> => {
  try {
    const url = `${BASE_URL}/lob/${localStorage.getItem("lob")}`;
    const lobDataResponse: AxiosResponse<ILobData> = await axios({
      url,
      method: "GET",
    });
    return {
      step: lobDataResponse.data.onboardingStep ?? 2,
      mandatoryConfigSetupProgress: lobDataResponse.data.mandatoryConfigSetupProgress ?? 0,
      isOnboarding: lobDataResponse.data.isOnboarding ?? false
    }
  } catch (e) {
    return {
      step: 2,
      isOnboarding: false,
      mandatoryConfigSetupProgress: 0
    }
  }
};

export const updateOnboardingStep = async (step: number) : Promise<boolean> => {
  try {
    const url = `${BASE_URL}/lob/updateOnboardingStep`;
    const lobDataResponse: AxiosResponse<ILobData> = await axios({
      url,
      method: "PUT",
      data: {
        lob: localStorage.getItem("lob"),
        onboardingStep: step
      }
    });

    if (lobDataResponse.status === 201) {
      return true;
    }

    return false;
  } catch (e) {
    return false;
  }
}
export const updateProgressBarPoint = async (points: number) : Promise<boolean> => {
  try {
    const url = `${BASE_URL}/lob/updateMandatoryConfigSetupProgress`;
    const lobDataResponse: AxiosResponse<ILobData> = await axios({
      url,
      method: "PUT",
      data: {
        lob: localStorage.getItem("lob"),
        mandatoryConfigSetupProgress: points
      }
    });

    if (lobDataResponse.status === 201) {
      return true;
    }

    return false;
  } catch (e) {
    return false;
  }
}

export const updateOnboardingStatus = async (isOnboarding: boolean) : Promise<boolean> => {
  try {
    const url = `${BASE_URL}/lob/updateOnboardingStatus`;
    const lobDataResponse: AxiosResponse<ILobData> = await axios({
      url,
      method: "PUT",
      data: {
        lob: localStorage.getItem("lob"),
        isOnboarding
      }
    });

    if (lobDataResponse.status === 201) {
      return true;
    }

    return false;
  } catch (e) {
    return false;
  }
}
export async function getPwaAutoLoginConfig(){
  const url = '/metadata/portal_configuration';
  const domainType = "pwa_autologin_configuration";
  const configName = "rolesWisePWAInfo";
  try{
      const response = await ChannelkartNetworkGet(url);
      const config = response.data.features.find((Obj: { domainType: string }) => {
          return Obj.domainType === domainType;
      })
      return config?.domainValues?.find((option: { name: string }) => {
        return option.name === configName
      })?.value;
      
  }catch(err){
      console.log(err);
      return null;
  }   
}
export const getIframeToken = async (role?:string) : Promise<string | null> => {
  try {  
    const clientConfig = await getMetaDataConfig("clientconfig");
    const portal_configuration = getConfigFromClientConfig(clientConfig,"portal_configuration");
    const roleWisePwaInfoInClientConfig = portal_configuration?.find((option: { name: string }) => {
      return option.name === "rolesWisePWACredentials"
    })?.value;
    const roleWisePwaConfigInMetaData = await getPwaAutoLoginConfig();
    let rolesWisePWAInfo: any = null;
    if(roleWisePwaConfigInMetaData){
      rolesWisePWAInfo = roleWisePwaConfigInMetaData;
    }else if(roleWisePwaInfoInClientConfig){
      rolesWisePWAInfo = roleWisePwaInfoInClientConfig;
    }else{
      return null;
    }
    let PWAId = ""
    let PWAPassword = ""
    if (role && rolesWisePWAInfo) {
      PWAId = rolesWisePWAInfo?.[role]?.id
      PWAPassword = rolesWisePWAInfo?.[role]?.password
    }

    if(!PWAId || !PWAPassword || PWAId==="" || PWAPassword===""){
      return null;
    }

    const lob = EnvironmentRegistry.getCurrentLob();
    const env = EnvironmentRegistry.getEnvironment();

    const loginResp = await signInForUserAndEnv(PWAId, PWAPassword, lob!, env!);
    return loginResp.data.token;
  } catch (e) {
    return null;
  }
}

export const isStandardLob = (portalConfig: any) : boolean => {
  const portalConfigValues: configurationAttributeType[] =
        portalConfig?.domainValues;
  return portalConfigValues.find((item) => item.name === "subscriptionType")
          ?.value! === "standard";
}

export const getCompanyDesignations = (
  portalConfig: any
): string[] => {
  const portalConfigValues: configurationAttributeType<unknown>[] = portalConfig?.domainValues;

  const designationItem = portalConfigValues?.find(
    (item) => item.name === "companyDesignations" && typeof item?.value === "string"
  );

  const designation = designationItem?.value as string | undefined;

  return designation?.split(',') ?? ["company", "reportadmin"];
};

export const isDeltaEnabled = (portalConfig: any) : boolean => {
  const portalConfigValues: configurationAttributeType[] =
        portalConfig?.domainValues;
  const delta = portalConfigValues && portalConfigValues?.find((item: { name: string; }) => item?.name === "useDeltaDataSync")?.value
  if(delta){
    return (delta as boolean);
  }else{
    return false;
  }
}

export const getDataSyncManagerConfig = (portalConfig: any) : DataSyncManagerConfig[] | undefined => {
  const portalConfigValues: configurationAttributeType[] =
        portalConfig?.domainValues;
  const delta = portalConfigValues && portalConfigValues?.find((item: { name: string; }) => item?.name === "dataSyncManagerConfig")?.value
  if(delta){
    return (delta as DataSyncManagerConfig[]);
  }else{
    return undefined;
  }
}

export const isProductionMovementCompleted = (portalConfig: any) : boolean => {
  const portalConfigValues: configurationAttributeType[] =
        portalConfig?.domainValues;
  return portalConfigValues.find((item) => item.name === "productionMovement")
          ?.value! === true;
}

export const getDataByModel = async (model: string): Promise<any[]> => {
  switch (model) {
    case "OutletDetails":
      return await fetchOutletDetails();
    default:
      return [];
  }
}

export const updateProductionMovement = async (status: boolean, successCallback: () => Promise<void>,newUi?:boolean): Promise<void> => {
  const portalConfig = await getMetaDataConfig(
    "clientconfig",
    "portal_configuration"
  );

  const portalConfigValues: configurationAttributeType<boolean>[] =
        portalConfig?.domainValues;
  const productionMovementConfig = portalConfigValues.find((item) => item.name === "productionMovement") as unknown as configurationAttributeType<boolean> | undefined;

  if (productionMovementConfig) {
    productionMovementConfig.value = status;
  } else {
    portalConfigValues.push({
      name: "productionMovement",
      possibleVals: [
        true, false
      ],
      description: "",
      docUrl: "",
      type: "boolean",
      value: true
    });
  }

  const finalPayload: metaDataBatchPayload = {
    features: [{
      domainName: "clientconfig",
      domainType: "portal_configuration",
      domainValues: portalConfigValues,
      lob: getLob()
    }],
  }

  await validateWithOtp(
    async (verifiedUser?: manageUpdateAccessObj, verifyResponse?: any) => {
      try {
        if (verifyResponse.status >= 200 || verifyResponse.status < 300) {
          const validationObj = validateMetaDataResponse(
            verifyResponse?.data?.features
          );
          if (validationObj.success) {
            await successCallback();
          } else {
            openPopup("Error", validationObj.message);
          }
        } else {
          throw new Error("Something went wrong");
        }
      } catch (err) {
        // Sentry.captureException(err);
        if (err instanceof Error) openPopup("Error", err?.message);
      }
    },
    undefined,
    "admin",
    META_DATA_BATCH_API,
    finalPayload,
    "PUT",
    newUi
  );
}

export const showProductionMovementOption = (): boolean => {
  const currentUserData = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user;
  const designations = currentUserData?.designation ?? [];

  if (designations.includes("admin") || designations.includes("projectadmin")) {
    return true;
  }

  return false;
}
